import * as React from "react"
import { ResponsiveContainer } from "recharts"

import { cn } from "@/shared/lib/utils"

export type ChartConfig = Record<string, { color?: string; label?: React.ReactNode }>

type ChartContainerProps = React.ComponentProps<"div"> & {
  config: ChartConfig
  id?: string
  children?: React.ReactNode
  empty?: React.ReactNode
}

export function ChartContainer({
  config,
  className,
  children,
  empty,
  id,
  ...props
}: ChartContainerProps) {
  const uniqueId = React.useId()
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, "")}`
  const showEmpty = empty !== undefined

  return (
    <div
      className={cn(
        "relative flex w-full min-w-0 min-h-[1px] justify-center text-xs [&_.recharts-layer]:outline-hidden [&_.recharts-surface]:outline-hidden",
        className,
      )}
      data-chart={chartId}
      {...props}
    >
      <ChartStyle config={config} id={chartId} />
      {showEmpty ? (
        <div className="flex h-full min-h-[150px] w-full items-center justify-center text-sm text-[var(--color-text-secondary)]">
          {empty}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      )}
    </div>
  )
}

const ChartStyle = ({ id, config }: { id: string; config: ChartConfig }) => {
  const entries = Object.entries(config).filter(([, value]) => value.color)

  if (entries.length === 0) {
    return null
  }

  const styles = entries.map(([key, value]) => `  --color-${key}: ${value.color};`).join("\n")

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `[data-chart=${id}] {\n${styles}\n}`,
      }}
    />
  )
}

export { ChartStyle }
